import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Car,
  Calendar,
  Phone,
  Mail,
  Share2,
  Heart,
  Layout,
  Printer,
} from "lucide-react";
import { useData } from "../contexts/DataContext";
import GenericHero from "../components/GenericHero";
import Navigation from "../components/Navigation";
import { supabase } from "@/lib/supabaseClient";
import { useCurrency } from "../contexts/CurrencyContext";
import LoanCalculator from "../components/LoanCalculator";
import ModernGallery from "../components/ModernGallery";

const PropertyDetailPage = () => {
  const { id } = useParams();
  const { properties, apartments, listingTeam, loading } = useData();
  const { formatPrice } = useCurrency();
  const [isLiked, setIsLiked] = useState(false);
  const [property, setProperty] = useState(null);
  const [showInvestmentCalculator, setShowInvestmentCalculator] = useState(false);
  const [showMapCard, setShowMapCard] = useState(false);
  const renderPrice = (price, fallback = "Contact for Price") => {
    if (!price) return fallback;
    return formatPrice(price);
  };

  useEffect(() => {
    const allProperties = [...(properties || []), ...(apartments || [])];
    if (allProperties.length > 0) {
      const foundProperty = allProperties.find((p) => p.id.toString() === id);
      setProperty(foundProperty);
    }
  }, [id, properties, apartments]);

  useEffect(() => {
    // Hide map card when switching properties
    setShowMapCard(false);
  }, [property?.id]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-800 mx-auto mb-4"></div>
          <p className="text-stone-600">Loading property...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-semibold text-stone-800">Property not found</h2>
          <Link to="/properties">
            <Button className="bg-[#81be00] text-stone-900 hover:bg-[#6a9e00]">
              Back to Properties
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const displayImages =
    property.images && property.images.length > 0
      ? property.images
      : property.featured_image
      ? [property.featured_image]
      : [];

  const heroBg =
    property.featured_image ||
    property.images?.[0] ||
    "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";
  // If property has a direct video URL (mp4 or hosted video), use it for the hero
  const heroVideo = property.video_url && (property.video_url.includes('.mp4') || property.video_url.includes('ik.imagekit.io') || property.video_url.includes('nyinimu'))
    ? property.video_url
    : null;
  const galleryItems = displayImages.map((src, idx) => ({
    src,
    alt: `${property.title || "Property"} image ${idx + 1}`,
    caption: property.location || property.short_description || "",
  }));

  const amenitiesList = Array.isArray(property.amenities)
    ? property.amenities
    : [];

  const listingLabel = (property.status || "").toLowerCase().includes("rent")
    ? "Rent"
    : "Sales";
  const normalizedCategory = (property.category || "").toLowerCase();
  const supportsMapLink = ["house", "houses", "home", "homes", "land", "plot", "plots"].includes(normalizedCategory);

  const activeListingTeam = Array.isArray(listingTeam)
    ? listingTeam.filter((member) => member?.is_active !== false)
    : [];
  const sortedListingTeam = activeListingTeam
    .slice()
    .sort((a, b) => (a?.display_order ?? 9999) - (b?.display_order ?? 9999));
  
  // First, try to find the specifically assigned agent for this property
  const assignedAgent = property.selected_agent_id 
    ? sortedListingTeam.find((agent) => agent.id === property.selected_agent_id)
    : null;
  
  // Use the assigned agent if found, otherwise fall back to the first active team member
  const defaultAgent = assignedAgent || sortedListingTeam[0];

  const agentName =
    property.contact_name ||
    property.agent_name ||
    property.owner_name ||
    property.listing_agent ||
    defaultAgent?.name ||
    "Listing Team";
  const agentTitle =
    property.contact_title ||
    property.agent_title ||
    property.owner_title ||
    defaultAgent?.title ||
    "Listing Agent";
  const agentAvatar =
    property.agent_avatar ||
    property.owner_avatar ||
    defaultAgent?.avatar_url ||
    defaultAgent?.image_url ||
    defaultAgent?.photo ||
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80";
  const agentPhone =
    property.agent_phone ||
    property.contact_phone ||
    property.owner_phone ||
    property.phone ||
    defaultAgent?.phone ||
    "";
  const agentEmail =
    property.agent_email ||
    property.contact_email ||
    property.owner_email ||
    property.email ||
    defaultAgent?.email ||
    "";

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    {
      label: property.category ? property.category : "Properties",
      href: "/properties",
    },
    { label: property.title, href: "#" },
  ];

  return (
    <div className="bg-[#f7f7f7] min-h-screen pt-20">
      <Navigation />

      {/* Hero Section with video background */}
      <div className="absolute inset-0 h-[40vh] w-full -z-10">
        <img
          src={heroBg}
          alt="Property Hero"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>
      <div className="pt-0">
        <GenericHero
          title={property.title}
          description={property.short_description || property.location || "Discover your dream property"}
          backgroundVideo={heroVideo}
          backgroundImage={!heroVideo ? heroBg : undefined}
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-16 space-y-10">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-stone-500 pt-6">
          {breadcrumbItems.map((crumb, idx) => (
            <React.Fragment key={crumb.label}>
              {idx !== 0 && <span className="text-stone-400">/</span>}
              {crumb.href === "#" ? (
                <span className="text-stone-700 font-medium">{crumb.label}</span>
              ) : (
                <Link to={crumb.href} className="hover:text-stone-800">
                  {crumb.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>

        <section className="bg-white rounded-3xl shadow-sm p-6 lg:p-10 space-y-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200 text-xs sm:text-sm">
                  {listingLabel}
                </Badge>
                {property.category && (
                  <Badge variant="outline" className="border-stone-200 text-stone-600 capitalize text-xs sm:text-sm">
                    {property.category}
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">
                {property.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-stone-600 text-sm sm:text-base">
                <p className="flex items-center">
                  <MapPin size={16} className="mr-2 text-[#81be00] flex-shrink-0" />
                  <span className="break-words">{property.location || "Location available on request"}</span>
                </p>
                {property.google_location && supportsMapLink && (
                  <Button
                    className="bg-[#81be00] text-black hover:bg-[#6a9e00] h-8 sm:h-9 px-3 text-xs sm:text-sm"
                    onClick={() => setShowMapCard((open) => !open)}
                  >
                    {showMapCard ? "Hide Map" : "View on Google Maps"}
                  </Button>
                )}
              </div>
              {showMapCard && property.google_location && supportsMapLink && (
                <div className="mt-3 w-full max-w-xl bg-white rounded-2xl shadow-lg border border-stone-200 p-3">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-semibold text-stone-800">Location Preview</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setShowMapCard(false)}
                    >
                      Close
                    </Button>
                  </div>
                  <div className="rounded-xl overflow-hidden border border-stone-100">
                    <iframe
                      title="Google Maps"
                      src={`https://www.google.com/maps?q=${encodeURIComponent(property.google_location)}&output=embed`}
                      className="w-full h-64"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col items-start lg:items-end gap-4">
              <div className="text-2xl sm:text-3xl font-semibold text-stone-900">
                {renderPrice(property.price)}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-10 w-10">
                  <Share2 size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsLiked((prev) => !prev)}
                  className={`h-10 w-10 ${isLiked ? "text-red-500 border-red-200" : ""}`}
                >
                  <Heart size={16} fill={isLiked ? "currentColor" : "none"} />
                </Button>
                <Button variant="outline" size="icon" className="h-10 w-10">
                  <Printer size={16} />
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_320px]">
            <div className="space-y-10">
              {displayImages.length > 0 && <ModernGallery items={galleryItems} />}

              {/* Video Section */}
              {property.video_url && (
                <div className="space-y-4">
                  <h3 className="text-xl sm:text-2xl font-semibold text-stone-900">Property Video</h3>
                  <div className="rounded-2xl sm:rounded-3xl overflow-hidden">
                    {property.video_url.includes('youtube.com') || property.video_url.includes('youtu.be') ? (
                      // YouTube embed
                      <iframe
                        src={property.video_url.replace('watch?v=', 'embed/')}
                        className="w-full h-[250px] sm:h-[350px] md:h-[500px]"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Property Video"
                      />
                    ) : property.video_url.includes('vimeo.com') ? (
                      // Vimeo embed
                      <iframe
                        src={property.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                        className="w-full h-[250px] sm:h-[350px] md:h-[500px]"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title="Property Video"
                      />
                    ) : (
                      // Direct video file
                      <video
                        src={property.video_url}
                        className="w-full h-[250px] sm:h-[350px] md:h-[500px] object-cover"
                        controls
                        preload="metadata"
                        title="Property Video"
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                {property.category === "land" ? (
                  <>
                    <StatCard
                      icon={<Square className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label={`Size (${property.land_size_unit || "acres"})`}
                      value={property.land_size || "N/A"}
                    />
                    <StatCard
                      icon={<MapPin className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label="Property Type"
                      value="Land"
                    />
                    <StatCard
                      icon={<Calendar className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label="Status"
                      value={property.status || "Available"}
                    />
                    <StatCard
                      icon={<Layout className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label="Category"
                      value="Plot"
                    />
                  </>
                ) : (
                  <>
                    <StatCard
                      icon={<Bed className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label="Bedrooms"
                      value={property.bedrooms || "N/A"}
                    />
                    <StatCard
                      icon={<Bath className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label="Bathrooms"
                      value={property.bathrooms || "N/A"}
                    />
                    <StatCard
                      icon={<Square className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label="Area (sqft)"
                      value={property.area || "N/A"}
                    />
                    <StatCard
                      icon={<Car className="w-5 sm:w-6 h-5 sm:h-6" />}
                      label="Parking"
                      value={property.parking || "Included"}
                    />
                  </>
                )}
              </div>

              {property.short_description && (
                <p className="text-lg text-stone-600 leading-relaxed italic bg-stone-50 rounded-2xl p-6">
                  {property.short_description}
                </p>
              )}

              {property.description && (
                <div className="space-y-3">
                  <h3 className="text-2xl font-semibold text-stone-900">
                    Description
                  </h3>
                  <p className="text-stone-600 text-lg leading-relaxed whitespace-pre-line">
                    {property.description}
                  </p>
                </div>
              )}

              {amenitiesList.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-2xl font-semibold text-stone-900">
                    Amenities & Features
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {amenitiesList.map((amenity, index) => (
                      <div
                        key={index}
                        className="flex items-center text-stone-600"
                      >
                        <div className="w-2 h-2 bg-[#81be00] mr-3 rounded-full"></div>
                        {amenity}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="space-y-6">
              <Card className="border border-stone-100 shadow-lg">
                <CardContent className="p-6 space-y-4">
                  {/* NyiNimu Calculator positioned at the top */}
                  <Button 
                    onClick={() => setShowInvestmentCalculator(true)}
                    className="w-full bg-[#81be00] text-black hover:bg-[#6a9e00] font-semibold py-3 mb-4"
                  >
                    NyiNimu Calculator
                  </Button>
                  
                  <div className="flex items-center gap-4">
                    <img
                      src={agentAvatar}
                      alt={agentName}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-lg font-semibold text-stone-900">{agentName}</p>
                      <p className="text-sm text-stone-500">{agentTitle}</p>
                    </div>
                  </div>
                  
                  <Button className="w-full bg-stone-900 hover:bg-stone-800 text-white">
                    Schedule a showing?
                  </Button>
                  <div className="flex flex-col gap-3">
                    {agentPhone && (
                      <a href={`tel:${agentPhone}`}>
                        <Button variant="outline" className="w-full">
                          <Phone size={16} className="mr-2" /> Call
                        </Button>
                      </a>
                    )}
                    {agentEmail && (
                      <a href={`mailto:${agentEmail}`}>
                        <Button variant="outline" className="w-full">
                          <Mail size={16} className="mr-2" /> Email
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-stone-100 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Send a message</h3>
                  <PropertyContactForm propertyId={property.id} />
                </CardContent>
              </Card>
            </aside>
          </div>
        </section>
      </div>

      {/* NyiNimu Calculator Modal */}
      {showInvestmentCalculator && (
        <LoanCalculator 
          propertyPrice={property.price} 
          isModal={true}
          onClose={() => setShowInvestmentCalculator(false)}
        />
      )}
    </div>
  );
};

// Inline contact form component
function PropertyContactForm({ propertyId }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    setError(null);
    try {
      const { error: supaError } = await supabase.from('contact_messages').insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        property_id: propertyId,
        source: 'property_detail',
      });
      if (supaError) throw supaError;
      setSuccess('Message sent! We will get back to you.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left">
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-2">Full name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter your full name"
          className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-2">E-mail</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-2">Phone</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Enter your phone number"
          className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-2">Message</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="4"
          placeholder="Your message"
          className="w-full px-4 py-3 bg-transparent border-b border-stone-300 focus:ring-0 focus:border-[#81be00] transition-all duration-300 placeholder:text-stone-400"
          required
        />
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-[#81be00] hover:bg-[#6a9e00] text-stone-900">
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>
      {success && <p className="text-sm text-green-600">{success}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </form>
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-lg sm:rounded-2xl border border-stone-200 bg-white p-3 sm:p-4 text-center shadow-sm">
    <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-full bg-stone-50 text-stone-700 flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div className="text-lg sm:text-2xl font-semibold text-stone-900 break-words">{value}</div>
    <p className="text-xs sm:text-sm text-stone-500 mt-1">{label}</p>
  </div>
);

export default PropertyDetailPage;
